import type { AssistantOption, AssistantState } from '../services/assistantService'
import i18next from 'i18next'

export type AssistantInputType =
  | 'text'
  | 'project_select'
  | 'device_select'
  | 'tag_select'
  | 'confirm'
export type ChatWidgetUiLanguage = 'fi' | 'en' | 'swe' | 'de' | 'no' | 'fr' | 'nl' | 'es'

export type UserCoordinates = {
  latitude: number
  longitude: number
}

function normalizeChatWidgetUiLanguage(language?: string): ChatWidgetUiLanguage {
  const raw = (language ?? '').trim().toLowerCase().replace(/_/g, '-')

  if (!raw) {
    return 'fi'
  }

  if (raw === 'fi' || raw.startsWith('fi-')) {
    return 'fi'
  }

  if (raw === 'en' || raw.startsWith('en-')) {
    return 'en'
  }

  if (raw === 'swe' || raw === 'sv' || raw.startsWith('swe') || raw.startsWith('sv-')) {
    return 'swe'
  }

  if (raw === 'de' || raw.startsWith('de-')) {
    return 'de'
  }

  if (raw === 'no' || raw === 'nb' || raw.startsWith('no-') || raw.startsWith('nb-')) {
    return 'no'
  }

  if (raw === 'fr' || raw.startsWith('fr-')) {
    return 'fr'
  }

  if (raw === 'nl' || raw.startsWith('nl-')) {
    return 'nl'
  }

  if (raw === 'es' || raw.startsWith('es-')) {
    return 'es'
  }

  return 'fi'
}

const CHAT_WIDGET_UI_LANGUAGES: ChatWidgetUiLanguage[] = [
  'fi',
  'en',
  'swe',
  'de',
  'no',
  'fr',
  'nl',
  'es'
]

const DEFAULT_PAGE_QUICK_REPLY_PROMPTS = [
  'I want to install a new RuuviTag',
  'I want to install a VALVE device',
  'I want to install a SPINE device',
  'How do I choose the right project and device?',
  'Can I scan the device QR code?',
  'How do I read the device code from an image with AI?'
]
const DEFAULT_FLOATING_QUICK_REPLY_PROMPTS = [
  'I need help getting started',
  'Where can I find more information?',
  'How do I open the AI agent for installation?',
  'Where is the manual installation guide?',
  'Who are you?'
]
const DEFAULT_PAGE_WELCOME_MESSAGE = 'Hi! I am the AI agent. Tell me how I can help.'
const DEFAULT_FLOATING_WELCOME_MESSAGE =
  'Hi! This quick chat helps with small questions and extra information. For installations I will route you to the AI agent or manual guidance.'
const DEFAULT_CHAT_PANEL_TITLE = { page: 'AI agent', floating: 'Quick chat' }
const DEFAULT_CHAT_PANEL_SUBTITLE = {
  page: 'Bedrock-guided installation',
  floating: 'General help and additional information'
}
const DEFAULT_YES_NO_PROMPTS: [string, string] = ['Yes', 'No']
const DEFAULT_START_OVER_PROMPT = 'Start over'
const DEFAULT_GO_BACK_PROMPT = 'Go back one step'
const DEFAULT_RETRY_PROMPT = 'Try again'
const DEFAULT_QUICK_PROMPT_SECTION_TITLE = 'Choose the next step:'
const DEFAULT_AI_INTERPRETATION_CONFIRM_TITLE = 'Confirm AI interpretation:'
const DEFAULT_ASSISTANT_TYPING_LABEL = 'AI agent is typing...'
const DEFAULT_INSTALL_MODE_PROMPTS = [
  'Automatic installation (AI agent)',
  'Manual installation',
  'Cancel'
]

export const AUTOMATIC_INSTALL_MODE_PROMPT = DEFAULT_INSTALL_MODE_PROMPTS[0]
export const MANUAL_INSTALL_MODE_PROMPT = DEFAULT_INSTALL_MODE_PROMPTS[1]
export const CANCEL_INSTALL_MODE_PROMPT = DEFAULT_INSTALL_MODE_PROMPTS[2]
export const INSTALL_MODE_PROMPTS = [...DEFAULT_INSTALL_MODE_PROMPTS]

function getInstallationTranslator(language?: string) {
  const normalizedLanguage = normalizeChatWidgetUiLanguage(language)
  return i18next.getFixedT(normalizedLanguage, 'installation')
}

function getLocalizedString(language: string | undefined, key: string, fallback: string): string {
  const translated = getInstallationTranslator(language)(key) as unknown

  return typeof translated === 'string' && translated.trim().length > 0 ? translated : fallback
}

function getLocalizedStringArray(
  language: string | undefined,
  key: string,
  fallback: string[]
): string[] {
  const translated = getInstallationTranslator(language)(key, { returnObjects: true }) as unknown

  if (!Array.isArray(translated)) {
    return fallback
  }

  const normalized = translated
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter((item) => item.length > 0)

  return normalized.length > 0 ? normalized : fallback
}

export function getInstallModePrompts(language?: string): string[] {
  const prompts = getLocalizedStringArray(
    language,
    'chatWidgetInstallModePrompts',
    DEFAULT_INSTALL_MODE_PROMPTS
  )

  return prompts.length >= 3 ? prompts.slice(0, 3) : DEFAULT_INSTALL_MODE_PROMPTS
}

export function getLocalizedYesNoPrompts(language?: string): [string, string] {
  const prompts = getLocalizedStringArray(
    language,
    'chatWidgetYesNoPrompts',
    DEFAULT_YES_NO_PROMPTS
  )

  return [prompts[0] ?? DEFAULT_YES_NO_PROMPTS[0], prompts[1] ?? DEFAULT_YES_NO_PROMPTS[1]]
}

export function getLocalizedStartOverPrompt(language?: string): string {
  return getLocalizedString(language, 'chatWidgetStartOverPrompt', DEFAULT_START_OVER_PROMPT)
}

export function getLocalizedGoBackPrompt(language?: string): string {
  return getLocalizedString(language, 'chatWidgetGoBackPrompt', DEFAULT_GO_BACK_PROMPT)
}

export function appendNavigationPrompts(prompts: string[], language?: string): string[] {
  const normalizedPrompts = prompts
    .map((prompt) => prompt.trim())
    .filter((prompt) => prompt.length > 0)

  const goBackPrompt = getLocalizedGoBackPrompt(language)
  const startOverPrompt = getLocalizedStartOverPrompt(language)

  return Array.from(new Set([...normalizedPrompts, goBackPrompt, startOverPrompt]))
}

export function getLocalizedRetryPrompt(language?: string): string {
  return getLocalizedString(language, 'chatWidgetRetryPrompt', DEFAULT_RETRY_PROMPT)
}

export function getLocalizedQuickPromptSectionTitle(
  language?: string,
  mode: 'default' | 'confirm_ai' = 'default'
): string {
  return mode === 'confirm_ai'
    ? getLocalizedString(
        language,
        'chatWidgetAiInterpretationConfirmTitle',
        DEFAULT_AI_INTERPRETATION_CONFIRM_TITLE
      )
    : getLocalizedString(
        language,
        'chatWidgetQuickPromptSectionTitle',
        DEFAULT_QUICK_PROMPT_SECTION_TITLE
      )
}

export function getLocalizedAssistantTypingLabel(language?: string): string {
  return getLocalizedString(
    language,
    'chatWidgetAssistantTypingLabel',
    DEFAULT_ASSISTANT_TYPING_LABEL
  )
}

export function getLocalizedChatPanelTitle(isPageVariant: boolean, language?: string): string {
  const pageTitle = getLocalizedString(
    language,
    'chatWidgetPanelTitlePage',
    DEFAULT_CHAT_PANEL_TITLE.page
  )
  const floatingTitle = getLocalizedString(
    language,
    'chatWidgetPanelTitleFloating',
    DEFAULT_CHAT_PANEL_TITLE.floating
  )

  return isPageVariant ? pageTitle : floatingTitle
}

export function getLocalizedChatPanelSubtitle(isPageVariant: boolean, language?: string): string {
  const pageSubtitle = getLocalizedString(
    language,
    'chatWidgetPanelSubtitlePage',
    DEFAULT_CHAT_PANEL_SUBTITLE.page
  )
  const floatingSubtitle = getLocalizedString(
    language,
    'chatWidgetPanelSubtitleFloating',
    DEFAULT_CHAT_PANEL_SUBTITLE.floating
  )

  return isPageVariant ? pageSubtitle : floatingSubtitle
}

function calculateDistanceMeters(from: UserCoordinates, to: UserCoordinates): number {
  const EARTH_RADIUS_METERS = 6371e3
  const toRadians = (value: number) => (value * Math.PI) / 180

  const latitudeDelta = toRadians(to.latitude - from.latitude)
  const longitudeDelta = toRadians(to.longitude - from.longitude)
  const fromLatitude = toRadians(from.latitude)
  const toLatitude = toRadians(to.latitude)

  const a =
    Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
    Math.cos(fromLatitude) *
      Math.cos(toLatitude) *
      Math.sin(longitudeDelta / 2) *
      Math.sin(longitudeDelta / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return EARTH_RADIUS_METERS * c
}

export function rankProjectOptionsByDistance(
  options: AssistantOption[],
  projects: Array<{ id: number; lat: number | null; long: number | null }>,
  userCoordinates: UserCoordinates | null
): AssistantOption[] {
  if (options.length === 0) {
    return options
  }

  const projectContextById = new Map(projects.map((project) => [project.id, project]))

  return options
    .map((option, index) => {
      const projectId = Number(option.value)
      const projectMeta = Number.isFinite(projectId) ? projectContextById.get(projectId) : undefined
      const projectLatitude = projectMeta?.lat !== null ? Number(projectMeta?.lat) : NaN
      const projectLongitude = projectMeta?.long !== null ? Number(projectMeta?.long) : NaN

      const hasProjectCoordinates =
        projectMeta !== undefined &&
        Number.isFinite(projectLatitude) &&
        Number.isFinite(projectLongitude)

      const distanceMeters =
        userCoordinates && hasProjectCoordinates
          ? calculateDistanceMeters(userCoordinates, {
              latitude: projectLatitude,
              longitude: projectLongitude
            })
          : Number.POSITIVE_INFINITY

      return {
        option,
        index,
        distanceMeters
      }
    })
    .sort((a, b) => {
      if (a.distanceMeters !== b.distanceMeters) {
        return a.distanceMeters - b.distanceMeters
      }

      return a.index - b.index
    })
    .map((entry) => entry.option)
}

export function getDefaultQuickReplyPrompts(isPageVariant: boolean, language?: string): string[] {
  return isPageVariant
    ? getLocalizedStringArray(
        language,
        'chatWidgetDefaultQuickReplyPromptsPage',
        DEFAULT_PAGE_QUICK_REPLY_PROMPTS
      )
    : getLocalizedStringArray(
        language,
        'chatWidgetDefaultQuickReplyPromptsFloating',
        DEFAULT_FLOATING_QUICK_REPLY_PROMPTS
      )
}

export function getWelcomeMessage(isPageVariant: boolean, language?: string): string {
  return isPageVariant
    ? getLocalizedString(language, 'chatWidgetWelcomeMessagePage', DEFAULT_PAGE_WELCOME_MESSAGE)
    : getLocalizedString(
        language,
        'chatWidgetWelcomeMessageFloating',
        DEFAULT_FLOATING_WELCOME_MESSAGE
      )
}

export function buildGuidedPrompts(
  state?: AssistantState,
  inputType: AssistantInputType = 'text',
  fallbackPrompts: string[] = DEFAULT_PAGE_QUICK_REPLY_PROMPTS,
  language?: string
): string[] {
  const normalizedLanguage = normalizeChatWidgetUiLanguage(language)
  const tInstallation = i18next.getFixedT(normalizedLanguage, 'installation')
  const startOverPrompt = getLocalizedStartOverPrompt(language)
  const withNavigationPrompts = (prompts: string[]): string[] =>
    appendNavigationPrompts(prompts, language)

  const getPromptList = (key: string, fallback: string[] = fallbackPrompts): string[] => {
    const resolved = tInstallation(key, { returnObjects: true }) as unknown

    if (!Array.isArray(resolved)) {
      return fallback
    }

    const prompts = resolved
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter((item) => item.length > 0)

    return prompts.length > 0 ? prompts : fallback
  }

  const withStartOverPrompt = (key: string, fallback: string[] = fallbackPrompts): string[] => {
    return withNavigationPrompts([...getPromptList(key, fallback), startOverPrompt])
  }

  if (inputType === 'confirm') {
    return withNavigationPrompts([...getLocalizedYesNoPrompts(language)])
  }

  if (inputType === 'project_select') {
    return withStartOverPrompt('chatWidgetGuidedPrompts.projectSelect')
  }

  if (inputType === 'device_select') {
    if (
      state?.workflow === 'device_install' &&
      state.step === 'ask_device' &&
      (state.deviceType === 'SPINE' || state.deviceType === 'VALVE')
    ) {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceSelect.askDeviceSpineOrValve')
    }

    if (
      state?.workflow === 'device_install' &&
      state.step === 'confirm_existing_device_installation'
    ) {
      return withStartOverPrompt(
        'chatWidgetGuidedPrompts.deviceSelect.confirmExistingDeviceInstallation'
      )
    }

    if (state?.workflow === 'device_install' && state.step === 'confirm_use_existing_valve_tags') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceSelect.confirmUseExistingValveTags')
    }

    if (state?.workflow === 'device_install' && state.step === 'ask_spine_parent_power_supply') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceSelect.askSpineParentPowerSupply')
    }

    return withStartOverPrompt('chatWidgetGuidedPrompts.deviceSelect.default')
  }

  if (inputType === 'tag_select') {
    return withStartOverPrompt('chatWidgetGuidedPrompts.tagSelect')
  }

  if (state?.workflow === 'ruuvitag_install') {
    if (state.step === 'ask_spine_tag_mount') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.ruuvitagInstall.askSpineTagMount')
    }

    if (state.step === 'ask_valve_tag_role') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.ruuvitagInstall.askValveTagRole')
    }

    if (state.step === 'ask_asset_name') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.ruuvitagInstall.askAssetName')
    }

    if (state.step === 'confirm') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.ruuvitagInstall.confirm')
    }

    return withStartOverPrompt('chatWidgetGuidedPrompts.ruuvitagInstall.default')
  }

  if (state?.workflow === 'device_install') {
    if (
      state.step === 'ask_device' &&
      (state.deviceType === 'SPINE' || state.deviceType === 'VALVE')
    ) {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.askDeviceSpineOrValve')
    }

    if (state.step === 'ask_device_type') {
      return withNavigationPrompts(
        getPromptList('chatWidgetGuidedPrompts.deviceInstall.askDeviceType')
      )
    }

    if (state.step === 'ask_install_target') {
      return withNavigationPrompts(
        getPromptList('chatWidgetGuidedPrompts.deviceInstall.askInstallTarget')
      )
    }

    if (state.step === 'ask_spine_name') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.askSpineName')
    }

    if (state.step === 'ask_spine_level') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.askSpineLevel')
    }

    if (state.step === 'edit_valve_level') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.askSpineLevel')
    }

    if (state.step === 'ask_spine_lift') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.askSpineLift')
    }

    if (state.step === 'edit_valve_lift') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.askSpineLift')
    }

    if (state.step === 'ask_spine_input_cables') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.askSpineInputCables')
    }

    if (state.step === 'ask_spine_main_distribution') {
      return withNavigationPrompts([...getLocalizedYesNoPrompts(language)])
    }

    if (state.step === 'ask_spine_parent_power_supply') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.askSpineParentPowerSupply')
    }

    if (state.step === 'ask_valve_customer_reference') {
      return getPromptList('chatWidgetGuidedPrompts.deviceInstall.askValveCustomerReference')
    }

    if (state.step === 'ask_valve_heater_details') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.askValveHeaterDetails')
    }

    if (state.step === 'ask_valve_heater_name') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.askValveHeaterName')
    }

    if (state.step === 'ask_valve_heater_manufacturer') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.askValveHeaterManufacturer')
    }

    if (state.step === 'ask_valve_heater_product_code') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.askValveHeaterProductCode')
    }

    if (state.step === 'ask_valve_heater_power') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.askValveHeaterPower')
    }

    if (state.step === 'ask_valve_in_tag_id') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.askValveInTagId')
    }

    if (state.step === 'ask_valve_out_tag_id') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.askValveOutTagId')
    }

    if (state.step === 'ask_valve_asset_details') {
      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.askValveAssetDetails')
    }

    if (state.step === 'confirm') {
      if (state.deviceType === 'SPINE') {
        return getPromptList('chatWidgetGuidedPrompts.deviceInstall.confirmSpine')
      }

      if (state.deviceType === 'VALVE') {
        return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.confirmValve')
      }

      return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.confirmDefault')
    }

    return withStartOverPrompt('chatWidgetGuidedPrompts.deviceInstall.default')
  }

  return withNavigationPrompts(fallbackPrompts)
}

export function isInstallationReference(message: string): boolean {
  const lowered = message.toLowerCase()

  return [
    'asenna',
    'asennus',
    'asentaa',
    'liittää',
    'liita',
    'liitän',
    'liitan',
    'kytkeä',
    'kytke',
    'ottaa käyttöön',
    'kayttoonotto',
    'laiteasennus',
    'manuaaliasennus',
    'automaattinen asennus',
    'install',
    'installation',
    'setup',
    'set up'
  ].some((keyword) => lowered.includes(keyword))
}

export function detectInstallModeChoice(message: string): 'automatic' | 'manual' | 'cancel' | null {
  const lowered = message.toLowerCase().trim()
  const normalizedMessage = normalizeComparableText(message)

  const localizedInstallModePrompts = CHAT_WIDGET_UI_LANGUAGES.map((languageCode) =>
    getInstallModePrompts(languageCode)
  )
  const defaultInstallModePrompts = getInstallModePrompts('fi')
  const automaticPrompt = defaultInstallModePrompts[0] ?? AUTOMATIC_INSTALL_MODE_PROMPT
  const manualPrompt = defaultInstallModePrompts[1] ?? MANUAL_INSTALL_MODE_PROMPT
  const cancelPrompt = defaultInstallModePrompts[2] ?? CANCEL_INSTALL_MODE_PROMPT
  const automaticPromptAliases = localizedInstallModePrompts.map((prompts) =>
    prompts[0].toLowerCase()
  )
  const manualPromptAliases = localizedInstallModePrompts.map((prompts) => prompts[1].toLowerCase())
  const cancelPromptAliases = localizedInstallModePrompts.map((prompts) => prompts[2].toLowerCase())

  if (
    [
      automaticPrompt.toLowerCase(),
      ...automaticPromptAliases,
      'automaattinen',
      'automaatti',
      'automatic',
      'ai-agentti',
      'ai asennus',
      'ai-asennus'
    ].some((keyword) => lowered.includes(keyword))
  ) {
    return 'automatic'
  }

  if (
    [manualPrompt.toLowerCase(), ...manualPromptAliases, 'manuaali', 'manual'].some((keyword) =>
      lowered.includes(keyword)
    )
  ) {
    return 'manual'
  }

  if (
    [cancelPrompt.toLowerCase(), ...cancelPromptAliases, 'peruuta', 'cancel'].some((keyword) =>
      lowered.includes(keyword)
    )
  ) {
    return 'cancel'
  }

  if (
    automaticPromptAliases.some((keyword) =>
      normalizedMessage.includes(normalizeComparableText(keyword))
    )
  ) {
    return 'automatic'
  }

  if (
    manualPromptAliases.some((keyword) =>
      normalizedMessage.includes(normalizeComparableText(keyword))
    )
  ) {
    return 'manual'
  }

  if (
    cancelPromptAliases.some((keyword) =>
      normalizedMessage.includes(normalizeComparableText(keyword))
    )
  ) {
    return 'cancel'
  }

  return null
}

export function isInstallModeQuestionReply(replyText: string): boolean {
  const normalizedReply = normalizeComparableText(replyText)
  const localizedInstallModePrompts = CHAT_WIDGET_UI_LANGUAGES.map((languageCode) =>
    getInstallModePrompts(languageCode)
  )
  const hasAutomaticPrompt = localizedInstallModePrompts.some((prompts) =>
    normalizedReply.includes(normalizeComparableText(prompts[0]))
  )
  const hasManualPrompt = localizedInstallModePrompts.some((prompts) =>
    normalizedReply.includes(normalizeComparableText(prompts[1]))
  )

  if (hasAutomaticPrompt && hasManualPrompt) {
    return true
  }

  const lowered = replyText.toLowerCase()

  return (
    (lowered.includes('automaattinen') || lowered.includes('automatic')) &&
    (lowered.includes('manuaali') || lowered.includes('manual')) &&
    lowered.includes('asenn')
  )
}

export function isTooGeneralRequestReply(replyText: string): boolean {
  const lowered = replyText.toLowerCase()

  return (
    lowered.includes('request is too general') ||
    (lowered.includes('too general') && lowered.includes('more specific information'))
  )
}

export function detectFloatingInstallShortcut(
  message: string,
  language?: string
): 'open_ai_agent' | 'open_manual' | null {
  const lowered = message.toLowerCase()
  const normalizedMessage = normalizeComparableText(message)
  const promptAliases = CHAT_WIDGET_UI_LANGUAGES.map((lang) =>
    getDefaultQuickReplyPrompts(false, lang)
  )
  const activeLanguagePrompts = getDefaultQuickReplyPrompts(false, language)
  const aiAgentPromptAliases = promptAliases
    .map((prompts) => prompts[2])
    .filter((prompt): prompt is string => typeof prompt === 'string' && prompt.trim().length > 0)
    .map((prompt) => normalizeComparableText(prompt))
  const manualPromptAliases = promptAliases
    .map((prompts) => prompts[3])
    .filter((prompt): prompt is string => typeof prompt === 'string' && prompt.trim().length > 0)
    .map((prompt) => normalizeComparableText(prompt))
  const asksLocationOrOpen = ['mista', 'miten', 'avaa', 'avata', 'loytyy', 'where', 'how', 'open']
    .map((keyword) => normalizeComparableText(keyword))
    .some((keyword) => normalizedMessage.includes(keyword))

  if (
    activeLanguagePrompts[2] &&
    normalizedMessage.includes(normalizeComparableText(activeLanguagePrompts[2]))
  ) {
    return 'open_ai_agent'
  }

  if (
    activeLanguagePrompts[3] &&
    normalizedMessage.includes(normalizeComparableText(activeLanguagePrompts[3]))
  ) {
    return 'open_manual'
  }

  if (aiAgentPromptAliases.some((alias) => normalizedMessage.includes(alias))) {
    return 'open_ai_agent'
  }

  if (manualPromptAliases.some((alias) => normalizedMessage.includes(alias))) {
    return 'open_manual'
  }

  if (
    lowered.includes('manuaaliohjaus') ||
    (lowered.includes('manuaali') && (lowered.includes('ohjaus') || asksLocationOrOpen))
  ) {
    return 'open_manual'
  }

  if (
    lowered.includes('ai-agentti') ||
    lowered.includes('ai agentti') ||
    lowered.includes('ai-agentin') ||
    lowered.includes('ai agentin')
  ) {
    return 'open_ai_agent'
  }

  return null
}

export function buildFloatingHelpReply(message: string, language?: string): string | null {
  const lowered = message.toLowerCase()
  const normalizedMessage = normalizeComparableText(message)
  const normalizedLanguage = normalizeChatWidgetUiLanguage(language)
  const isFinnish = normalizedLanguage === 'fi'
  const floatingPrompts = getDefaultQuickReplyPrompts(false, language)

  const gettingStartedPrompt = floatingPrompts[0] ?? DEFAULT_FLOATING_QUICK_REPLY_PROMPTS[0]
  const moreInfoPrompt = floatingPrompts[1] ?? DEFAULT_FLOATING_QUICK_REPLY_PROMPTS[1]
  const whoAreYouPrompt = floatingPrompts[4] ?? DEFAULT_FLOATING_QUICK_REPLY_PROMPTS[4]

  const wantsGettingStartedHelp =
    normalizedMessage.includes(normalizeComparableText(gettingStartedPrompt)) ||
    lowered.includes('tarvitsen apua käyttöön') ||
    lowered.includes('apua käyttöön') ||
    lowered.includes('need help getting started')

  const wantsMoreInfo =
    normalizedMessage.includes(normalizeComparableText(moreInfoPrompt)) ||
    lowered.includes('lisätietoja') ||
    lowered.includes('mistä löydän lisätietoja') ||
    lowered.includes('more information')

  const asksWhoAreYou =
    normalizedMessage.includes(normalizeComparableText(whoAreYouPrompt)) ||
    lowered.includes('kuka olet') ||
    lowered.includes('who are you')

  if (wantsGettingStartedHelp) {
    return isFinnish
      ? [
          'Totta kai, autan mielelläni.',
          'Jos haluat asentaa laitteen, kirjoita siitä niin ohjaan sinut AI-agenttiin tai manuaaliohjaukseen.',
          'Muissa asioissa kerro lyhyesti mitä yrität tehdä (esim. projekti, laite, hälytykset tai käyttäjät).'
        ].join(' ')
      : [
          'Sure, happy to help.',
          'If you want to install a device, tell me and I will route you to the AI agent or manual guidance.',
          'For other topics, tell me briefly what you are trying to do (for example project, device, alerts, or users).'
        ].join(' ')
  }

  if (wantsMoreInfo) {
    return isFinnish
      ? 'Kerro aihe, josta tarvitset lisätietoja, niin annan tarkat ohjeet. Asennuksissa ohjaan sinut AI-agenttiin tai manuaaliohjaukseen.'
      : 'Tell me what topic you need more information about and I will provide clear steps. For installations I will route you to the AI agent or manual guidance.'
  }

  if (asksWhoAreYou) {
    return isFinnish
      ? 'Olen pikachat, joka auttaa pienissä kysymyksissä. Asennuspolut ohjaan AI-agenttiin tai manuaaliohjaukseen.'
      : 'I am the quick chat for small questions. For installation flows I route you to the AI agent or manual guidance.'
  }

  return null
}

function normalizeComparableText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

export function findProjectByTypedName(
  projects: Array<{ id: number; name: string }>,
  typedInput: string
): { id: number; name: string } | null {
  const normalizedInput = normalizeComparableText(typedInput)

  if (!normalizedInput) {
    return null
  }

  const exactNormalizedMatches = projects.filter(
    (project) => normalizeComparableText(project.name) === normalizedInput
  )

  if (exactNormalizedMatches.length === 1) {
    return exactNormalizedMatches[0]
  }

  return null
}

export function isCannotFindDeviceMessage(message: string): boolean {
  const lowered = message.toLowerCase().trim()
  const normalizedMessage = normalizeComparableText(message)
  const localizedCannotFindPrompts = CHAT_WIDGET_UI_LANGUAGES.flatMap((languageCode) =>
    getLocalizedStringArray(languageCode, 'chatWidgetGuidedPrompts.deviceSelect.default', [
      'I cannot find the correct device'
    ])
  )

  if (
    localizedCannotFindPrompts.some((prompt) =>
      normalizedMessage.includes(normalizeComparableText(prompt))
    )
  ) {
    return true
  }

  return (
    lowered === 'en löydä oikeaa laitetta' ||
    lowered.includes('en löydä laitetta') ||
    lowered.includes('ei löydy oikeaa laitetta') ||
    lowered.includes('cannot find the correct device')
  )
}

export function buildDeviceNotFoundReply(deviceCount: number, language?: string): string {
  const isFinnish = normalizeChatWidgetUiLanguage(language) === 'fi'
  const lines = isFinnish
    ? [
        'Selvä, etsitään oikea laite toisella tavalla.',
        'Voit tehdä yhden näistä heti:',
        '1) Skannaa laitekoodi kameralla',
        '2) Syötä laitekoodi tai laitteen nimi manuaalisesti chattiin'
      ]
    : [
        "Okay, let's find the correct device in another way.",
        'You can do one of these right away:',
        '1) Scan the device code with camera',
        '2) Enter the device code or device name manually in chat'
      ]

  if (deviceCount === 0) {
    lines.push(
      isFinnish
        ? 'Tässä projektissa ei näy laitteita. Varmista projekti tai aloita alusta.'
        : 'No devices are visible in this project. Verify the project or start over.'
    )
  } else {
    lines.push(
      isFinnish
        ? 'Jos laite ei kuulu tähän projektiin, vaihda projekti tai aloita alusta.'
        : 'If the device does not belong to this project, switch project or start over.'
    )
  }

  return lines.join('\n')
}

export function parseYesNoAnswer(value: string): boolean | null {
  const normalized = normalizeComparableText(value)

  if (
    ['kylla', 'k', 'yes', 'y', 'ok', 'okei', 'juu', 'joo', 'ja', 'oui', 'si'].includes(normalized)
  ) {
    return true
  }

  if (['ei', 'e', 'no', 'n', 'nej', 'nei', 'non', 'nee'].includes(normalized)) {
    return false
  }

  return null
}

function extractBulletSuggestionsFromAssistantReply(replyText: string): string[] {
  const lines = replyText
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)

  const bulletSuggestions = lines
    .filter((line) => /^([-*•]|\d+\.)\s+/.test(line))
    .map((line) => line.replace(/^([-*•]|\d+\.)\s+/, '').trim())
    .filter((line) => line.length > 0 && line.length <= 120)

  return Array.from(new Set(bulletSuggestions)).slice(0, 4)
}

export function buildSpineNameQuickPromptsFromReply(replyText: string): string[] | null {
  const suggestions = extractBulletSuggestionsFromAssistantReply(replyText)
  const suggestionPrompts = suggestions.filter(
    (suggestion) => suggestion !== 'Anna nimiehdotuksia' && suggestion !== 'Aloita alusta'
  )

  if (suggestionPrompts.length === 0) {
    return null
  }

  return Array.from(new Set(suggestionPrompts))
}

function normalizeRuuviTagId(rawValue: string): string {
  return rawValue.replace(/[^0-9a-fA-F]/g, '').toUpperCase()
}

export function extractRuuviTagIdFromQr(rawValue: string): string | null {
  const macWithSeparatorsMatch = rawValue.match(/([0-9a-fA-F]{2}[:-]){5}[0-9a-fA-F]{2}/)

  if (macWithSeparatorsMatch?.[0]) {
    const normalized = normalizeRuuviTagId(macWithSeparatorsMatch[0])
    return normalized.length === 12 ? normalized : null
  }

  const plainMacMatch = rawValue.match(/\b[0-9a-fA-F]{12}\b/)

  if (plainMacMatch?.[0]) {
    const normalized = normalizeRuuviTagId(plainMacMatch[0])
    return normalized.length === 12 ? normalized : null
  }

  return null
}

export function normalizeAssistantReply(text: string): string {
  if (!text) {
    return text
  }

  if (!text.includes('u00') && !text.includes('\\u')) {
    return text
  }

  return text.replace(/\\u([0-9a-fA-F]{4})|u([0-9a-fA-F]{4})/g, (_match, escaped, plain) => {
    const hex = escaped ?? plain

    try {
      return String.fromCharCode(parseInt(hex, 16))
    } catch (_error) {
      return _match
    }
  })
}

export function extractReplyFromJsonLike(raw: string): string | null {
  const cleaned = raw.replace(/```json|```/gi, '').trim()
  const firstCurlyIndex = cleaned.indexOf('{')
  const lastCurlyIndex = cleaned.lastIndexOf('}')

  if (firstCurlyIndex >= 0 && lastCurlyIndex > firstCurlyIndex) {
    try {
      const parsed = JSON.parse(cleaned.slice(firstCurlyIndex, lastCurlyIndex + 1)) as {
        reply?: string
      }

      if (typeof parsed.reply === 'string' && parsed.reply.trim().length > 0) {
        return parsed.reply.trim()
      }
    } catch (_error) {
      // Fall through to regex recovery.
    }
  }

  const replyMatch = cleaned.match(/"reply"\s*:\s*"([\s\S]*?)"\s*,\s*"state"\s*:/)

  if (!replyMatch?.[1]) {
    return null
  }

  return replyMatch[1]
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t')
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, '\\')
    .trim()
}
